import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../Services/loginService";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Fade,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import api, { API_BASE_URL } from "../../src/utils/api";
import IntroLoader from "../IntroLoader/IntroLoader";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [showIntroLoader, setShowIntroLoader] = useState(true);

  const [forgotEmail, setForgotEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [requestName, setRequestName] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleLogin = async () => {
    try {
      const user = await loginUser(username, password);

      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("roleId", user.roleId);

      if (rememberMe) {
        localStorage.setItem("savedUsername", username);
      } else {
        localStorage.removeItem("savedUsername");
      }

      setTimeout(() => {
        if (user.roleId === 3) {
          navigate("/home/memberspage", { replace: true });
        } else {
          navigate("/home", { replace: true });
        }
      }, 0);
    } catch (error) {
      setErrorMessage("Invalid username or password");
      setOpenSnackbar(true);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      setErrorMessage("Please enter your email.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send reset email");
      }

      setEmailSent(true);
      setTimeout(() => {
        setShowForgotPasswordModal(false);
        setEmailSent(false);
        setForgotEmail("");
      }, 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setOpenSnackbar(true);
    }
  };

  const handleRequestAccess = async () => {
    if (!requestName || !requestEmail || !requestReason) {
      setErrorMessage("All fields are required.");
      setOpenSnackbar(true);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(requestEmail)) {
      setErrorMessage("Please enter a valid email.");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`${API_BASE_URL}/request-access`, {
        name: requestName,
        email: requestEmail,
        reason: requestReason,
      });

      if (response.status === 200) {
        setRequestSuccess(true);
        setTimeout(() => {
          setShowRequestAccessModal(false);
          setRequestSuccess(false);
          setRequestName("");
          setRequestEmail("");
          setRequestReason("");
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to submit request.");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "An error occurred.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedUsername = localStorage.getItem("savedUsername");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    const timer = setTimeout(() => {
      setShowIntroLoader(false);
    }, 2200);

    return () => clearTimeout(timer);
  }, []);

  const dialogPaperSx = {
    borderRadius: isMobile ? "18px 18px 0 0" : "20px",
    background: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(5px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.20)",
    overflow: "hidden",
    ...(isMobile && {
      m: 0,
      width: "100%",
      maxWidth: "100%",
      position: "absolute",
      bottom: 0,
    }),
  };

  const dialogTextFieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "12px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "12px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
  };

  if (showIntroLoader) {
    return <IntroLoader message="Loading your HOA portal..." />;
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#eef2f7",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: isMobile
            ? "url('/hoa_mobile_9x16.jpg')"
            : "url('/hoa.jpg')",
          backgroundSize: "cover",
          backgroundPosition: isMobile ? "center top" : "center center",
          backgroundRepeat: "no-repeat",
          filter: isMobile ? "blur(4px)" : "blur(6px)",
          transform: "scale(1.05)",
          transformOrigin: "center",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isMobile
            ? "linear-gradient(180deg, rgba(13,24,43,0.14), rgba(13,24,43,0.34))"
            : "linear-gradient(180deg, rgba(13,24,43,0.10), rgba(13,24,43,0.22))",
          backdropFilter: "blur(3.5px)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          minHeight: "100dvh",
          display: "flex",
          justifyContent: "center",
          alignItems: { xs: "flex-start", sm: "center" },
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 7, sm: 3 },
          pb: { xs: 4, sm: 3 },
          boxSizing: "border-box",
        }}
      >
        <Fade in timeout={500}>
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "360px", sm: "430px" },
              px: { xs: 2.25, sm: 4 },
              pt: { xs: 2.5, sm: 3 },
              pb: { xs: 3, sm: 6.5 },
              backgroundColor: "rgba(255,255,255,0.82)",
              borderRadius: { xs: "24px", sm: "20px" },
              boxShadow: {
                xs: "0 14px 40px rgba(0,0,0,0.22)",
                sm: "0 20px 60px rgba(0,0,0,0.30)",
              },
              textAlign: "center",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              boxSizing: "border-box",
              border: "1px solid rgba(255,255,255,0.38)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: { xs: 1.1, sm: 1.5 },
                mt: "0.15rem",
              }}
            >
              <Box
                component="img"
                src="/img/logo.png"
                alt="Logo"
                sx={{
                  width: { xs: "145px", sm: "220px" },
                  maxWidth: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.4,
                fontSize: { xs: "1.2rem", sm: "1.6rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Sign In
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#6b778c",
                mb: 2,
                px: 1,
                fontSize: { xs: "0.88rem", sm: "0.95rem" },
              }}
            >
              Access your HOA portal
            </Typography>

            <form method="post" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                autoComplete="username"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  mb: 1.1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.97)",
                    borderRadius: "12px",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 0.8,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.97)",
                    borderRadius: "12px",
                  },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: 1.25,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Remember Me"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.92rem",
                      color: "#444",
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#2f63ad",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "1rem",
                  py: 1.1,
                  borderRadius: "12px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#1f4f94",
                    boxShadow: "none",
                  },
                }}
              >
                Sign In
              </Button>
            </form>

            <Box
              sx={{
                mt: 1.6,
                display: "flex",
                justifyContent: "center",
                gap: 0.75,
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Link
                href="#"
                underline="hover"
                sx={{
                  fontSize: "0.9rem",
                  color: "#2f63ad",
                  cursor: "pointer",
                }}
                onClick={() => setShowForgotPasswordModal(true)}
              >
                Forgot your password?
              </Link>
{/*
              <Link
                href="#"
                underline="hover"
                sx={{
                  fontSize: "0.9rem",
                  color: "#2f63ad",
                  cursor: "pointer",
                }}
                onClick={() => setShowRequestAccessModal(true)}
              >
                Request Access
              </Link>
              */}
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 2.2,
                textAlign: "center",
                fontWeight: 500,
                color: "#666",
                fontSize: "0.86rem",
              }}
            >
              © My HOA {new Date().getFullYear()}
            </Typography>
          </Box>
        </Fade>
      </Box>

      <Dialog
        open={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Reset Password
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {emailSent ? (
            <Box sx={{ textAlign: "center", py: 1.5 }}>
              <Typography
                sx={{
                  color: "green",
                  fontWeight: 700,
                  fontSize: "1rem",
                }}
              >
                ✅ Reset link sent! Check your email.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Enter your email address and we’ll send you a reset link.
              </Typography>

              <TextField
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                variant="outlined"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                sx={dialogTextFieldSx}
              />
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          {!emailSent && (
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              sx={primaryButtonSx}
            >
              Send Reset Link
            </Button>
          )}
          <Button
            onClick={() => setShowForgotPasswordModal(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRequestAccessModal}
        onClose={() => setShowRequestAccessModal(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Request Access
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {requestSuccess ? (
            <Box sx={{ textAlign: "center", py: 1.5 }}>
              <Typography
                sx={{
                  color: "green",
                  fontWeight: 700,
                  fontSize: "1rem",
                }}
              >
                ✅ Request submitted! We will contact you soon.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Fill out the form below and our team will review your request.
              </Typography>

              <TextField
                fullWidth
                label="Full Name"
                variant="outlined"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                sx={dialogTextFieldSx}
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                variant="outlined"
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
                sx={dialogTextFieldSx}
              />

              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={4}
                variant="outlined"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                sx={dialogTextFieldSx}
              />
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          {!requestSuccess && (
            <Button
              onClick={handleRequestAccess}
              variant="contained"
              disabled={loading}
              sx={primaryButtonSx}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          )}
          <Button
            onClick={() => setShowRequestAccessModal(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;